export const buttonTheme = {
  root: {
    gap: '7px',
    borderRadius: 'var(--bs-border-radius-sm)',
    roundedBorderRadius: 'var(--bs-border-radius-sm)',
    // Sizing follows .btn in styles/components/buttons.scss, so both button
    // systems render at the same height: 1rem of padding on a 16px line plus
    // the 1px border gives the 50px the platform already uses.
    paddingX: '1rem',
    paddingY: '1rem',
    iconOnlyWidth: '50px',
    sm: {
      // .btn-sm: 30px tall, 12px type.
      fontSize: '0.75rem',
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      iconOnlyWidth: '50px'
    },
    lg: {
      fontSize: '1.125rem',
      paddingX: '1rem',
      paddingY: '0.5rem',
      iconOnlyWidth: '70px'
    },
    label: { fontWeight: '400' },
    raisedShadow: 'none',
    focusRing: {
      width: '0',
      style: 'none',
      offset: '0'
    },
    badgeSize: '1rem',
    transitionDuration: '{transition.duration}'
  },
  colorScheme: {
    light: {
      // The accent (#00bcef) is too light for white labels and for text on
      // white (2.2:1): labels on it are dark, accent and warning text take a
      // darker step via the --text-* tokens (MOG-4494).
      root: {
        primary: {
          background: 'var(--surface-accent-main)',
          hoverBackground: 'var(--surface-accent-main-darken)',
          activeBackground: 'var(--surface-accent-main)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-on-accent)',
          hoverColor: 'var(--text-on-accent)',
          activeColor: 'var(--text-on-accent)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        secondary: {
          background: 'transparent',
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          hoverBorderColor: 'var(--text-decent)',
          activeBorderColor: 'var(--text-decent)',
          color: 'var(--text-primary)',
          hoverColor: 'var(--text-primary)',
          activeColor: 'var(--text-primary)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        info: {
          background: 'var(--surface-accent-main)',
          hoverBackground: 'var(--surface-accent-main-darken)',
          activeBackground: 'var(--surface-accent-main)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-on-accent)',
          hoverColor: 'var(--text-on-accent)',
          activeColor: 'var(--text-on-accent)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        help: {
          background: 'var(--primary-pink)',
          hoverBorderColor: 'transparent',
          hoverColor: '{gray.0}',
          borderColor: 'transparent',
          color: '{gray.0}'
        },
        success: {
          background: 'var(--text-success)',
          hoverBackground: 'var(--text-success-dark)',
          activeBackground: 'var(--text-success)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-inside-fill)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-inside-fill)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        warn: {
          background: 'var(--warning)',
          hoverBackground: 'var(--mo-orange-700)',
          activeBackground: 'var(--warning)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-inside-fill)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-inside-fill)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        danger: {
          background: 'var(--text-error)',
          hoverBackground: 'var(--text-error-dark)',
          activeBackground: 'var(--text-error)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-inside-fill)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-inside-fill)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        contrast: {
          background: 'transparent',
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-primary)',
          hoverColor: 'var(--text-primary)',
          activeColor: 'var(--text-primary)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        }
      },
      outlined: {
        primary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-primary)'
        },
        secondary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-primary)'
        },
        success: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-success)'
        },
        info: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-accent-main)'
        },
        help: {
          borderColor: 'rgba(150, 150, 150, 1)',
          color: 'var(--text-primary)'
        },
        warn: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-warning)'
        },
        danger: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-error)'
        },
        contrast: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-primary)'
        }
      },
      text: {
        primary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-secondary)'
        },
        secondary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-secondary)'
        },
        success: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-success)'
        },
        info: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-accent-main)'
        },
        warn: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-warning)'
        },
        danger: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-error)'
        },
        contrast: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-accent-main)'
        }
      },
      link: {
        color: 'var(--text-accent-main)',
        hoverColor: 'var(--text-accent-main-darken)'
      }
    },
    dark: {
      root: {
        primary: {
          background: 'var(--surface-accent-main)',
          hoverBackground: 'var(--surface-accent-main-darken)',
          activeBackground: 'var(--surface-accent-main)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-inside-fill)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-inside-fill)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        secondary: {
          background: 'transparent',
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          hoverBorderColor: 'var(--text-decent)',
          activeBorderColor: 'var(--text-decent)',
          color: 'var(--text-primary)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-primary)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        info: {
          background: 'var(--surface-accent-main)',
          hoverBackground: 'var(--surface-accent-main-darken)',
          activeBackground: 'var(--surface-accent-main)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: '{gray.0}',
          hoverColor: '{gray.0}',
          activeColor: '{gray.0}',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        help: {
          background: 'var(--primary-pink)',
          hoverBorderColor: 'transparent',
          hoverColor: '{gray.0}',
          borderColor: 'transparent',
          color: '{gray.0}'
        },
        success: {
          background: 'var(--text-success)',
          hoverBackground: 'var(--text-success-dark)',
          activeBackground: 'var(--text-success)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-inside-fill)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-inside-fill)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        warn: {
          background: 'var(--warning)',
          hoverBackground: 'var(--mo-orange-700)',
          activeBackground: 'var(--warning)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-inside-fill)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-inside-fill)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        danger: {
          background: 'var(--text-error)',
          hoverBackground: 'var(--text-error-dark)',
          activeBackground: 'var(--text-error)',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-inside-fill)',
          hoverColor: 'var(--text-inside-fill)',
          activeColor: 'var(--text-inside-fill)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        },
        contrast: {
          background: 'transparent',
          hoverBackground: 'var(--bs-btn-hover-bg)',
          activeBackground: 'transparent',
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          activeBorderColor: 'transparent',
          color: 'var(--text-primary)',
          hoverColor: 'var(--text-primary)',
          activeColor: 'var(--text-primary)',
          focusRing: {
            color: 'transparent',
            shadow: 'none'
          }
        }
      },
      outlined: {
        primary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-primary)'
        },
        secondary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-primary)'
        },
        success: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-success)'
        },
        info: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--surface-accent-main)'
        },
        help: {
          borderColor: 'rgba(150, 150, 150, 1)',
          color: 'rgba(255, 255, 255, 1)'
        },
        warn: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--warning)'
        },
        danger: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-error)'
        },
        contrast: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          borderColor: 'var(--text-decent)',
          color: 'var(--text-primary)'
        }
      },
      text: {
        primary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-secondary)'
        },
        secondary: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-secondary)'
        },
        success: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-success)'
        },
        info: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--surface-accent-main)'
        },
        warn: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--warning)'
        },
        danger: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--text-error)'
        },
        contrast: {
          hoverBackground: 'var(--text-bg-hover-2)',
          activeBackground: 'transparent',
          color: 'var(--surface-accent-main)'
        }
      },
      link: {
        color: 'var(--surface-accent-main)',
        hoverColor: 'var(--surface-accent-main-darken)'
      }
    }
  },
  css: ({ dt }: { dt: any }) => `
  /* Button Base Styles */
    .p-button {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-shrink: 0 !important;
      outline: none !important;
      text-align: center !important;
      font-feature-settings: 'liga' off, 'clig' off !important;
      font-family: Roboto, Arial, Helvetica, sans-serif !important;
      font-style: normal !important;
      font-weight: 400 !important;
      letter-spacing: 2px !important;
      text-transform: uppercase !important;
      line-height: 100% !important;
      white-space: nowrap !important;
    }

    /* The same two-step ring .btn:focus-visible draws in buttons.scss. The
       outline stays off via focusRing.width: 0, as it is on .btn. */
    .p-button:focus-visible {
      box-shadow:
        0 0 0 2px var(--surface-background),
        0 0 0 4px var(--surface-accent-main);
    }

    .p-button-contrast:not(:disabled):hover {
      background: var(--text-bg-hover-2) !important;
    }
  `
};
